/**
 * @file
 * @fileOverview This file contains the user input components for the Aframe scene.
 * @version 1.0.0
 * @author Parker Eli Bidigare
 * @date 2024-05-26
 */

AFRAME.registerComponent("simple-zoom", {
    schema: {
      zoomFactor: { type: "number", default: 1.5 },
      zoomDuration: { type: "number", default: 0.2 },
    },
  
    init: function () {
      this.cameraEl = this.el.querySelector("a-camera");
      if (!this.cameraEl) {
        console.warn(
          "Camera element not found within the entity. Make sure there is an a-camera element.",
        );
        return;
      }
  
      this.camera = this.cameraEl.getObject3D("camera");
      if (!this.camera) {
        console.warn(
          "Camera object not found. Make sure the a-camera element has initialized properly.",
        );
        return;
      }
  
      this.originalFov = this.camera.fov;
      this.targetFov = this.originalFov;
      this.zoomed = false;
      this.zoomTime = 0;
  
      this.onKeyDown = this.onKeyDown.bind(this);
      window.addEventListener("keydown", this.onKeyDown);
  
      console.log("Simple zoom component initialized");
      console.log("Original FOV:", this.originalFov);
    },
  
    remove: function () {
      window.removeEventListener("keydown", this.onKeyDown);
    },
  
    onKeyDown: function (event) {
      if (event.key === "z") {
        console.log("Z key pressed, toggling zoom");
        this.toggleZoom();
      }
    },
  
    toggleZoom: function () {
      this.zoomed = !this.zoomed;
      this.targetFov = this.zoomed
        ? this.originalFov / this.data.zoomFactor
        : this.originalFov;
      this.zoomTime = 0;
      console.log(
        "Zoom toggled. Zoomed:",
        this.zoomed,
        "Target FOV:",
        this.targetFov,
      );
    },
  
    tick: function (time, deltaTime) {
      if (!this.camera) return;
  
      if (this.camera.fov !== this.targetFov) {
        this.zoomTime += deltaTime / 1000;
        const progress = Math.min(this.zoomTime / this.data.zoomDuration, 1);
  
        this.camera.fov = THREE.MathUtils.lerp(
          this.camera.fov,
          this.targetFov,
          progress,
        );
  
        this.camera.updateProjectionMatrix();
        console.log("Current FOV:", this.camera.fov);
      }
    },
  });

  AFRAME.registerComponent("advanced-camera-controls", {
    schema: {
      height: { type: "number", default: 1.6 },
      crouchHeight: { type: "number", default: 0.3 },
      speed: { type: "number", default: 1.5 },
      jumpForce: { type: "number", default: 5 },
      gravity: { type: "number", default: 9.8 },
      bobAmount: { type: "number", default: 0.05 },
      bobFrequency: { type: "number", default: 10 },
    },
  
    init: function () {
      this.camera = this.el.getObject3D("camera");
      this.cameraRig = this.el.object3D;
      this.velocity = new THREE.Vector3();
      this.moveVector = new THREE.Vector3();
      this.isJumping = false;
      this.isCrouching = false;
      this.isGrounded = true;
      this.currentHeight = this.data.height;
      this.bobPhase = 0;
      this.movementKeys = { 
        KeyW: false, 
        KeyA: false, 
        KeyS: false, 
        KeyD: false, 
        KeyUp: false, 
        KeyDown: false, 
        KeyLeft: false, 
        KeyRight: false 
      };
  
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.onBlur = this.onBlur.bind(this);
  
      this.isFocused = false;
      this.landingThreshold = 0.01; // Threshold for considering landing complete
      this.lastBobOffset = 0;
  
      this.sceneEl = this.el.sceneEl;
      this.sceneEl.addEventListener("focus", this.onFocus);
      this.sceneEl.addEventListener("blur", this.onBlur);
  
      window.addEventListener("keydown", this.onKeyDown);
      window.addEventListener("keyup", this.onKeyUp);
  
      this.cameraQuaternion = new THREE.Quaternion();
      this.groundLevel = 0;
      this.lastLogTime = 0;
  
      // Set initial camera position and base height
      this.baseHeight = this.data.height;
      this.camera.position.y = this.currentHeight;
    },
  
    onFocus: function () {
      this.isFocused = true;
    },
  
    onBlur: function () {
      this.isFocused = false;
      this.resetKeys();
    },
  
    resetKeys: function () {
      for (let key in this.movementKeys) {
        this.movementKeys[key] = false;
      }
      this.isCrouching = false;
    },
  
    tick: function (time, delta) {
      if (!this.isFocused) return;
  
      const deltaSeconds = delta / 1000;
      
      // Update movement first
      this.updateMovement(deltaSeconds);
      
      // Only update bobbing if we're fully grounded and not in any transition
      if (this.isGrounded && !this.isJumping && Math.abs(this.velocity.y) < this.landingThreshold) {
        this.updateBobbing(time);
      }
      
      // Update crouching last
      this.updateCrouching(deltaSeconds);
    },
  
    updateMovement: function (delta) {
      // Apply gravity when in air
      if (!this.isGrounded || this.isJumping) {
        this.velocity.y -= this.data.gravity * delta;
      }
  
      // Store the previous position for comparison
      const previousY = this.camera.position.y;
  
      // Apply vertical movement
      if (this.isJumping || !this.isGrounded) {
        this.camera.position.y += this.velocity.y * delta;
      }
  
      // Ground detection with smoothing
      const groundHeight = this.currentHeight;
      if (this.camera.position.y <= groundHeight) {
        // Smoothly settle to ground level
        const settleFactor = 0.2;
        this.camera.position.y = groundHeight;
        
        if (Math.abs(this.velocity.y) < this.landingThreshold) {
          this.velocity.y = 0;
          this.isGrounded = true;
          this.isJumping = false;
        } else {
          this.velocity.y *= settleFactor; // Dampen the velocity for smooth landing
        }
      } else {
        this.isGrounded = false;
      }
  
      // Calculate move vector based on input
      this.moveVector.set(0, 0, 0);
      if (this.movementKeys.KeyW) this.moveVector.z -= 1;
      if (this.movementKeys.KeyS) this.moveVector.z += 1;
      if (this.movementKeys.KeyA) this.moveVector.x -= 1;
      if (this.movementKeys.KeyD) this.moveVector.x += 1;
  
      // Apply movement with normalization
      if (this.moveVector.length() > 0) {
        this.moveVector.normalize().multiplyScalar(this.data.speed * delta);
  
        // Reduce speed while in air
        if (!this.isGrounded) {
          this.moveVector.multiplyScalar(0.8);
        }
      }
  
      // Apply camera rotation to movement
      this.cameraRig.getWorldQuaternion(this.cameraQuaternion);
      this.cameraQuaternion.x = 0;
      this.cameraQuaternion.z = 0;
      this.cameraQuaternion.normalize();
      this.moveVector.applyQuaternion(this.cameraQuaternion);
  
      // Apply horizontal movement
      this.cameraRig.position.add(this.moveVector);
    },
  
    updateBobbing: function (time) {
      if (this.isGrounded && (this.moveVector.x !== 0 || this.moveVector.z !== 0)) {
        this.bobPhase += this.data.bobFrequency * this.moveVector.length() * 0.01;
        const newBobOffset = Math.sin(this.bobPhase) * this.data.bobAmount;
        
        // Smoothly interpolate between bob offsets
        const bobDelta = newBobOffset - this.lastBobOffset;
        this.lastBobOffset = newBobOffset;
        
        // Only apply bobbing if we're fully grounded
        if (this.isGrounded && !this.isJumping) {
          this.camera.position.y = this.currentHeight + newBobOffset;
        }
      }
    },
  
    updateCrouching: function (delta) {
      const targetHeight = this.isCrouching ? this.data.crouchHeight : this.data.height;
      const heightDiff = targetHeight - this.currentHeight;
      const crouchSpeed = 2;
  
      if (Math.abs(heightDiff) > 0.01) {
        this.currentHeight += Math.sign(heightDiff) * crouchSpeed * delta;
        this.currentHeight = THREE.MathUtils.clamp(
          this.currentHeight,
          this.data.crouchHeight,
          this.data.height
        );
  
        // Only update camera height when grounded and not in transition
        if (this.isGrounded && Math.abs(this.velocity.y) < this.landingThreshold) {
          this.camera.position.y = this.currentHeight;
        }
      }
    },
  
    onKeyDown: function (event) {
      if (!this.isFocused) return;
  
      if (event.code in this.movementKeys) {
        this.movementKeys[event.code] = true;
      }
  
      switch (event.code) {
        case "Space":
          if (this.isGrounded && !this.isJumping) {
            this.velocity.y = this.data.jumpForce;
            this.isJumping = true;
            this.isGrounded = false;
            this.lastBobOffset = 0; // Reset bob offset when jumping
          }
          break;
        case "KeyC":
          if (this.isGrounded && !this.isJumping) {
            this.isCrouching = true;
          }
          break;
      }
    },
  
    onKeyUp: function (event) {
      if (!this.isFocused) return;
  
      if (event.code in this.movementKeys) {
        this.movementKeys[event.code] = false;
      }
  
      if (event.code === "KeyC") {
        this.isCrouching = false;
      }
    },
  
    remove: function () {
      window.removeEventListener("keydown", this.onKeyDown);
      window.addEventListener("keyup", this.onKeyUp);
      this.sceneEl.removeEventListener("focus", this.onFocus);
      this.sceneEl.removeEventListener("blur", this.onBlur);
    }
  });

  AFRAME.registerComponent('boundary-constraint', {
    schema: {
      shape: {type: 'string', default: 'rectangle', oneOf: ['circle', 'rectangle']},
      // For rectangle
      x1: {type: 'number', default: -10},
      x2: {type: 'number', default: 10},
      z1: {type: 'number', default: -10},
      z2: {type: 'number', default: 10},
      // For circle
      centerX: {type: 'number', default: 0},
      centerZ: {type: 'number', default: 0},
      radius: {type: 'number', default: 10},
      // Common
      y: {type: 'number', default: -0.4}
    },
  
    tick: function () {
      var data = this.data;
      var el = this.el;
      var position = el.object3D.position;
  
      if (data.shape === 'rectangle') {
        position.x = Math.max(data.x1, Math.min(data.x2, position.x));
        position.z = Math.max(data.z1, Math.min(data.z2, position.z));
      } else if (data.shape === 'circle') {
        var dx = position.x - data.centerX;
        var dz = position.z - data.centerZ;
        var distance = Math.sqrt(dx * dx + dz * dz);
        if (distance > data.radius) {
          var angle = Math.atan2(dz, dx);
          position.x = data.centerX + Math.cos(angle) * data.radius;
          position.z = data.centerZ + Math.sin(angle) * data.radius;
        }
      }
  
      position.y = data.y;
  
      el.object3D.position.set(position.x, position.y, position.z);
    }
  });

  AFRAME.registerComponent("unified-camera-controls", {
    schema: {
      height: { type: "number", default: 1.6 },
      crouchHeight: { type: "number", default: 0.3 },
      speed: { type: "number", default: 1.5 },
      jumpForce: { type: "number", default: 15 },
      gravity: { type: "number", default: 9.8 },
      bobAmount: { type: "number", default: 0.05 },
      bobFrequency: { type: "number", default: 10 },
      smoothFactor: { type: "number", default: 0.1 },
      touchLookSpeed: { type: "number", default: 0.1 },
      touchMoveThreshold: { type: "number", default: 10 },
      joystickSize: { type: "number", default: 100 },
      movementSpeed: { type: "number", default: 5 },
      enableJump: { type: "boolean", default: true },
    },
  
    init: function () {
      this.initVars();
      this.bindMethods();
      this.addEventListeners();
      this.createVirtualJoystick();
      this.updateJoystickPosition();
  
      this.el.addEventListener('loaded', this.onLoaded.bind(this));
    },
  
    initVars: function() {
      this.cameraRig = this.el;
      this.camera = null;
      this.cameraObj = null;
      this.rigObj = this.el.object3D;
      this.velocity = new THREE.Vector3();
      this.moveVector = new THREE.Vector3();
      this.isJumping = false;
      this.isCrouching = false;
      this.isGrounded = true;
      this.currentHeight = this.data.height;
      this.bobPhase = 0;
      this.movementKeys = { KeyW: false, KeyA: false, KeyS: false, KeyD: false };
      this.pitch = 0;
      this.yaw = 0;
      this.isMouseDown = false;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.isTouching = false;
      this.joystickTouch = null;
      this.isLandscape = window.innerWidth > window.innerHeight;
      this.isVR = false;
      this.vrMovement = new THREE.Vector3();
      this.targetPosition = new THREE.Vector3();
      this.currentPosition = new THREE.Vector3();
      this.vrHandedness = { left: null, right: null };
      this.thumbstickAxes = { left: new THREE.Vector2(), right: new THREE.Vector2() };
      this.cameraQuaternion = new THREE.Quaternion();
      this.groundLevel = 0;
      this.isFocused = true;
      this.forward = new THREE.Vector3();
      this.right = new THREE.Vector3();
      this.joystickMovement = new THREE.Vector2();
      this.lastValidCameraPosition = new THREE.Vector3();
      this.isJoystickActive = false;
      this.jumpCooldown = 0;
      this.lastJumpTime = 0;
      this.jumpInProgress = false;
    },
  
    bindMethods: function () {
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);
      this.onMouseDown = this.onMouseDown.bind(this);
      this.onMouseMove = this.onMouseMove.bind(this);
      this.onMouseUp = this.onMouseUp.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onOrientationChange = this.onOrientationChange.bind(this);
      this.onEnterVR = this.onEnterVR.bind(this);
      this.onExitVR = this.onExitVR.bind(this);
    },
  
    addEventListeners: function () {
      window.addEventListener("keydown", this.onKeyDown);
      window.addEventListener("keyup", this.onKeyUp);
      document.addEventListener('mousedown', this.onMouseDown);
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
      document.addEventListener('touchstart', this.onTouchStart, { passive: false });
      document.addEventListener('touchmove', this.onTouchMove, { passive: false });
      document.addEventListener('touchend', this.onTouchEnd, { passive: false });
      window.addEventListener('orientationchange', this.onOrientationChange);
      window.addEventListener('resize', this.onOrientationChange);
      this.el.sceneEl.addEventListener('enter-vr', this.onEnterVR);
      this.el.sceneEl.addEventListener('exit-vr', this.onExitVR);
    },
  
    onLoaded: function () {
      this.camera = this.el.querySelector('a-camera');
      if (!this.camera) {
        console.warn('Camera not found within the rig. Make sure there is an a-camera entity as a child of this entity.');
        return;
      }
      this.cameraObj = this.camera.object3D;
      this.updateCameraPosition();
    },
  
    updateCameraPosition: function() {
      if (this.cameraObj) {
        const newY = this.currentHeight - this.rigObj.position.y;
        if (!isNaN(newY)) {
          this.cameraObj.position.y = newY;
        }
      }
    },
  
    isMobileDevice() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      const hasTouchScreen = (
        'maxTouchPoints' in navigator && navigator.maxTouchPoints > 0
      ) || (
        'msMaxTouchPoints' in navigator && navigator.msMaxTouchPoints > 0
      );
      const hasSmallScreen = window.innerWidth < 768;
      return mobileRegex.test(userAgent) || hasTouchScreen || hasSmallScreen;
    },
  
    createVirtualJoystick: function () {
      if(!this.isMobileDevice()) return;
      this.joystickContainer = document.createElement('div');
      this.joystickContainer.style.position = 'fixed';
      this.joystickContainer.style.width = this.data.joystickSize + 'px';
      this.joystickContainer.style.height = this.data.joystickSize + 'px';
      this.joystickContainer.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
      this.joystickContainer.style.borderRadius = '50%';
      this.joystickContainer.style.zIndex = '9999';
      this.joystickContainer.style.touchAction = 'none';
  
      this.joystickKnob = document.createElement('div');
      this.joystickKnob.style.position = 'absolute';
      this.joystickKnob.style.width = '40%';
      this.joystickKnob.style.height = '40%';
      this.joystickKnob.style.backgroundColor = 'rgba(255, 255, 255, 0.5)';
      this.joystickKnob.style.borderRadius = '50%';
      this.joystickKnob.style.left = '30%';
      this.joystickKnob.style.top = '30%';
  
      this.joystickContainer.appendChild(this.joystickKnob);
      document.body.appendChild(this.joystickContainer);
  
      this.joystickContainer.addEventListener('touchstart', this.onJoystickTouchStart.bind(this), { passive: false });
      this.joystickContainer.addEventListener('touchmove', this.onJoystickTouchMove.bind(this), { passive: false });
      this.joystickContainer.addEventListener('touchend', this.onJoystickTouchEnd.bind(this), { passive: false });
    },
  
    updateJoystickPosition: function () {
      if(!this.isMobileDevice()) return;
      const margin = 20;
      const isLandscape = window.innerWidth > window.innerHeight;
      
      if (isLandscape) {
        this.joystickContainer.style.left = margin + 'px';
        this.joystickContainer.style.bottom = margin + 'px';
      } else {
        this.joystickContainer.style.left = margin + 'px';
        this.joystickContainer.style.bottom = margin + 'px';
      }
      
      this.joystickContainer.style.display = 'block';
    },
  
    onEnterVR: function () {
      this.isVR = true;
      this.setupVRControllers();
    },
  
    onExitVR: function () {
      this.isVR = false;
    },
  
    setupVRControllers: function () {
      const leftHand = document.querySelector('#leftHand');
      const rightHand = document.querySelector('#rightHand');
      if (leftHand) {
        this.vrHandedness.left = leftHand;
        leftHand.addEventListener('thumbstickmoved', this.onThumbstickMoved.bind(this, 'left'));
      }
      if (rightHand) {
        this.vrHandedness.right = rightHand;
        rightHand.addEventListener('thumbstickmoved', this.onThumbstickMoved.bind(this, 'right'));
      }
    },
  
    onThumbstickMoved: function (hand, event) {
      this.thumbstickAxes[hand].set(event.detail.x, event.detail.y);
    },
  
    onOrientationChange: function () {
      this.isLandscape = window.innerWidth > window.innerHeight;
      this.updateJoystickPosition();
    },
  
    onMouseDown: function (event) {
      this.isMouseDown = true;
    },
  
    onMouseMove: function (event) {
      if (!this.isMouseDown || this.isVR) return;
      this.yaw -= event.movementX * this.data.touchLookSpeed * 0.01;
      this.pitch -= event.movementY * this.data.touchLookSpeed * 0.01;
      this.pitch = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.pitch));
      this.updateCameraRotation();
    },
  
    onMouseUp: function (event) {
      this.isMouseDown = false;
    },
  
    onJoystickTouchStart: function (event) {
      event.preventDefault();
      const touch = event.touches[0];
      this.joystickTouch = touch.identifier;
      this.updateJoystickKnobPosition(touch.clientX, touch.clientY);
      this.isJoystickActive = true;
    },
  
    onJoystickTouchMove: function (event) {
      event.preventDefault();
      if (this.joystickTouch !== null) {
        const touch = Array.from(event.touches).find(t => t.identifier === this.joystickTouch);
        if (touch) {
          this.updateJoystickKnobPosition(touch.clientX, touch.clientY);
        }
      }
    },
  
    onJoystickTouchEnd: function (event) {
      event.preventDefault();
      this.joystickTouch = null;
      this.joystickMovement.set(0, 0);
      this.updateJoystickKnobPosition(0, 0);
      this.joystickKnob.style.transform = 'translate(0px, 0px)';
      this.isJoystickActive = false;
    },
  
    updateJoystickKnobPosition: function (clientX, clientY) {
      const rect = this.joystickContainer.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const maxDistance = rect.width / 2;
      let deltaX = clientX - centerX;
      let deltaY = clientY - centerY;
      const distance = Math.min(maxDistance, Math.sqrt(deltaX * deltaX + deltaY * deltaY));
      const angle = Math.atan2(deltaY, deltaX);
      const knobX = Math.cos(angle) * distance;
      const knobY = Math.sin(angle) * distance;
      this.joystickKnob.style.transform = `translate(${knobX}px, ${knobY}px)`;
      this.joystickMovement.set(knobX / maxDistance, -knobY / maxDistance);
    },
  
    onTouchStart: function (event) {
      if (event.touches.length === 1) {
        const touch = event.touches[0];
        this.isTouching = true;
        this.touchStartX = touch.pageX;
        this.touchStartY = touch.pageY;
      }
    },
  
    onTouchMove: function (event) {
      event.preventDefault();
      if (this.isTouching && event.touches.length === 1) {
        const touch = event.touches[0];
        const deltaX = touch.pageX - this.touchStartX;
        const deltaY = touch.pageY - this.touchStartY;
        if (Math.abs(deltaX) > this.data.touchMoveThreshold || Math.abs(deltaY) > this.data.touchMoveThreshold) {
          this.yaw -= deltaX * this.data.touchLookSpeed * 0.01;
          this.pitch -= deltaY * this.data.touchLookSpeed * 0.01;
          this.pitch = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.pitch));
          this.updateCameraRotation();
          this.touchStartX = touch.pageX;
          this.touchStartY = touch.pageY;
        }
      }
    },
  
    onTouchEnd: function (event) {
      this.isTouching = false;
    },
  
    onKeyDown: function (event) {
      if (event.code in this.movementKeys) {
        this.movementKeys[event.code] = true;
      }
      switch (event.code) {
        case "Space":
          if (this.data.enableJump && this.isGrounded && this.jumpCooldown <= 0 && !this.jumpInProgress) {
            this.velocity.y = this.data.jumpForce;
            this.isJumping = true;
            this.isGrounded = false;
            this.jumpCooldown = 0.3;
            this.jumpInProgress = true;
            this.lastJumpTime = performance.now();
          }
          break;
        case "KeyC":
          this.isCrouching = true;
          break;
      }
    },
  
    onKeyUp: function (event) {
      if (event.code in this.movementKeys) {
        this.movementKeys[event.code] = false;
      }
      if (event.code === "KeyC") {
        this.isCrouching = false;
      }
    },
  
    tick: function (time, delta) {
      if (!this.cameraObj) {
        this.onLoaded();
        if (!this.cameraObj) return;
      }
  
      const deltaSeconds = delta / 1000;
      
      this.updatePhysics(deltaSeconds);
      
      if (this.isVR) {
        this.updateVRMovement(deltaSeconds);
      } else {
        this.updateNonVRMovement(deltaSeconds);
      }
      
      this.updateBobbing(time);
      this.updateCrouching(deltaSeconds);
      
      const currentY = this.rigObj.position.y;
      this.rigObj.position.lerp(this.targetPosition, this.data.smoothFactor);
      
      this.rigObj.position.y = currentY;
      
      if (isNaN(this.rigObj.position.x) || isNaN(this.rigObj.position.y) || isNaN(this.rigObj.position.z)) {
        console.warn("Invalid position detected, restoring last valid position");
        if (this.lastValidCameraPosition) {
          this.rigObj.position.copy(this.lastValidCameraPosition);
        }
      } else {
        this.lastValidCameraPosition.copy(this.rigObj.position);
      }
      
      this.updateCameraPosition();
    },
  
    updateVRMovement: function (deltaSeconds) {
      const moveSpeed = this.data.speed * deltaSeconds;
      const combinedAxes = new THREE.Vector2(
        this.thumbstickAxes.left.x + this.thumbstickAxes.right.x,
        this.thumbstickAxes.left.y + this.thumbstickAxes.right.y
      ).clampLength(0, 1);
      const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.cameraRig.quaternion);
      const right = new THREE.Vector3(1, 0, 0).applyQuaternion(this.cameraRig.quaternion);
      this.vrMovement.set(0, 0, 0)
        .addScaledVector(forward, -combinedAxes.y * moveSpeed)
        .addScaledVector(right, combinedAxes.x * moveSpeed);
      this.targetPosition.copy(this.rigObj.position).add(this.vrMovement);
    },
  
    updateNonVRMovement: function (deltaSeconds) {
      const moveSpeed = this.data.speed * deltaSeconds;
      this.moveVector.set(0, 0, 0);
  
      if (this.el.sceneEl.is('vr-mode')) {
        if (this.movementKeys.KeyW) this.moveVector.z += this.data.movementSpeed;
        if (this.movementKeys.KeyS) this.moveVector.z -= this.data.movementSpeed;
        if (this.movementKeys.KeyA) this.moveVector.x -= this.data.movementSpeed;
        if (this.movementKeys.KeyD) this.moveVector.x += this.data.movementSpeed;
      } else if (this.el.sceneEl.isMobile) {
        if (this.isJoystickActive) {
          this.moveVector.x = this.joystickMovement.x * this.data.movementSpeed;
          this.moveVector.z = this.joystickMovement.y * this.data.movementSpeed;
        }
      } else {
        if (this.movementKeys.KeyW) this.moveVector.z += this.data.movementSpeed;
        if (this.movementKeys.KeyS) this.moveVector.z -= this.data.movementSpeed;
        if (this.movementKeys.KeyA) this.moveVector.x -= this.data.movementSpeed;
        if (this.movementKeys.KeyD) this.moveVector.x += this.data.movementSpeed;
      }
  
      if (this.moveVector.length() > 0) {
        this.moveVector.normalize().multiplyScalar(moveSpeed);
      }
  
      this.cameraObj.getWorldDirection(this.forward);
      this.right.crossVectors(this.forward, this.cameraObj.up);
  
      this.forward.y = 0;
      this.right.y = 0;
      this.forward.normalize();
      this.right.normalize();
  
      const movement = new THREE.Vector3();
      movement.addScaledVector(this.forward, -this.moveVector.z);
      movement.addScaledVector(this.right, -this.moveVector.x);
  
      if (movement.length() > 0) {
        this.targetPosition.copy(this.rigObj.position).add(movement);
      } else {
        this.targetPosition.copy(this.rigObj.position);
      }
    },
  
    updatePhysics: function (deltaSeconds) {
      if (!this.cameraObj) return;
  
      this.velocity.y -= this.data.gravity * deltaSeconds;
      
      const yDelta = this.velocity.y * deltaSeconds;
      this.rigObj.position.y += yDelta;
      
      if (this.rigObj.position.y <= this.groundLevel) {
        this.rigObj.position.y = this.groundLevel;
        if (this.velocity.y < 0) {
          this.velocity.y = 0;
          this.isGrounded = true;
          this.isJumping = false;
          this.jumpInProgress = false;
        }
      } else {
        this.isGrounded = false;
      }
  
      if (this.jumpCooldown > 0) {
        this.jumpCooldown -= deltaSeconds;
      }
      
      const jumpTimeout = 1000;
      if (this.jumpInProgress && performance.now() - this.lastJumpTime > jumpTimeout) {
        this.jumpInProgress = false;
      }
  
      this.updateCameraPosition();
    },
  
    updateBobbing: function (time) {
      if (!this.cameraObj) return;
  
      if (this.isGrounded && this.moveVector.length() > 0) {
        this.bobPhase += this.data.bobFrequency * this.moveVector.length() * 0.01;
        const bobOffset = Math.sin(this.bobPhase) * this.data.bobAmount;
        this.cameraObj.position.y += bobOffset;
      }
    },
  
    updateCrouching: function (deltaSeconds) {
      if (!this.cameraObj) return;
  
      const targetHeight = this.isCrouching ? this.data.crouchHeight : this.data.height;
      const heightDiff = targetHeight - this.currentHeight;
      const crouchSpeed = 2;
      if (Math.abs(heightDiff) > 0.01) {
        this.currentHeight += Math.sign(heightDiff) * crouchSpeed * deltaSeconds;
        this.currentHeight = THREE.MathUtils.clamp(
          this.currentHeight,
          this.data.crouchHeight,
          this.data.height
        );
        this.updateCameraPosition();
      }
    },
  
    updateCameraRotation: function () {
      if (!this.isVR && this.cameraObj) {
        this.cameraObj.rotation.set(this.pitch, this.yaw, 0, 'YXZ');
      }
    },
  
    remove: function () {
      window.removeEventListener("keydown", this.onKeyDown);
      window.removeEventListener("keyup", this.onKeyUp);
      document.removeEventListener('mousedown', this.onMouseDown);
      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.onMouseUp);
      document.removeEventListener('touchstart', this.onTouchStart);
      document.removeEventListener('touchmove', this.onTouchMove);
      document.removeEventListener('touchend', this.onTouchEnd);
      window.removeEventListener('orientationchange', this.onOrientationChange);
      window.removeEventListener('resize', this.onOrientationChange);
      this.el.sceneEl.removeEventListener('enter-vr', this.onEnterVR);
      this.el.sceneEl.removeEventListener('exit-vr', this.onExitVR);
      if (this.vrHandedness.left) {
        this.vrHandedness.left.removeEventListener('thumbstickmoved', this.onThumbstickMoved);
      }
      if (this.vrHandedness.right) {
        this.vrHandedness.right.removeEventListener('thumbstickmoved', this.onThumbstickMoved);
      }
      if (this.joystickContainer) {
        this.joystickContainer.removeEventListener('touchstart', this.onJoystickTouchStart);
        this.joystickContainer.removeEventListener('touchmove', this.onJoystickTouchMove);
        this.joystickContainer.removeEventListener('touchend', this.onJoystickTouchEnd);
        document.body.removeChild(this.joystickContainer);
      }
    }
  });


  AFRAME.registerComponent("unified-camera-controls2", {
    schema: {
      height: { type: "number", default: 1.6 },
      crouchHeight: { type: "number", default: 0.3 },
      speed: { type: "number", default: 1.5 },
      jumpForce: { type: "number", default: 15 },
      gravity: { type: "number", default: 9.8 },
      bobAmount: { type: "number", default: 0.05 },
      bobFrequency: { type: "number", default: 10 },
      smoothFactor: { type: "number", default: 0.1 },
      horizontalSmoothFactor: { type: "number", default: 0.1 }, // Separate smoothing for horizontal movement
      touchLookSpeed: { type: "number", default: 0.1 },
      touchMoveThreshold: { type: "number", default: 10 },
      joystickSize: { type: "number", default: 100 },
      movementSpeed: { type: "number", default: 5 },
    },
  
    init: function () {
      this.initVars();
      this.bindMethods();
      this.addEventListeners();
      this.createVirtualJoystick();
      this.updateJoystickPosition();
  
      this.el.addEventListener('loaded', this.onLoaded.bind(this));
    },
  
    initVars: function() {
      this.cameraRig = this.el;
      this.camera = null;
      this.cameraObj = null;
      this.rigObj = this.el.object3D;
      this.velocity = new THREE.Vector3();
      this.moveVector = new THREE.Vector3();
      this.isJumping = false;
      this.isCrouching = false;
      this.isGrounded = true;
      this.currentHeight = this.data.height;
      this.bobPhase = 0;
      this.movementKeys = { KeyW: false, KeyA: false, KeyS: false, KeyD: false };
      this.pitch = 0;
      this.yaw = 0;
      this.isMouseDown = false;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.isTouching = false;
      this.joystickTouch = null;
      this.isLandscape = window.innerWidth > window.innerHeight;
      this.isVR = false;
      this.vrMovement = new THREE.Vector3();
      this.targetPosition = new THREE.Vector3();
      this.currentPosition = new THREE.Vector3();
      this.vrHandedness = { left: null, right: null };
      this.thumbstickAxes = { left: new THREE.Vector2(), right: new THREE.Vector2() };
      this.cameraQuaternion = new THREE.Quaternion();
      this.groundLevel = 0;
      this.isFocused = true;
      this.forward = new THREE.Vector3();
      this.right = new THREE.Vector3();
      this.joystickMovement = new THREE.Vector2();
      this.lastValidCameraPosition = new THREE.Vector3();
      this.isJoystickActive = false;
      this.jumpCooldown = 0;
      this.horizontalMovement = new THREE.Vector3(); // Store horizontal movement separately
    },
  
    bindMethods: function () {
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);
      this.onMouseDown = this.onMouseDown.bind(this);
      this.onMouseMove = this.onMouseMove.bind(this);
      this.onMouseUp = this.onMouseUp.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);
      this.onOrientationChange = this.onOrientationChange.bind(this);
      this.onEnterVR = this.onEnterVR.bind(this);
      this.onExitVR = this.onExitVR.bind(this);
    },
  
    // [Event listener methods remain unchanged]
    addEventListeners: function () {
      window.addEventListener("keydown", this.onKeyDown);
      window.addEventListener("keyup", this.onKeyUp);
      document.addEventListener('mousedown', this.onMouseDown);
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
      document.addEventListener('touchstart', this.onTouchStart, { passive: false });
      document.addEventListener('touchmove', this.onTouchMove, { passive: false });
      document.addEventListener('touchend', this.onTouchEnd, { passive: false });
      window.addEventListener('orientationchange', this.onOrientationChange);
      window.addEventListener('resize', this.onOrientationChange);
      this.el.sceneEl.addEventListener('enter-vr', this.onEnterVR);
      this.el.sceneEl.addEventListener('exit-vr', this.onExitVR);
    },
  
    onLoaded: function () {
      this.camera = this.el.querySelector('a-camera');
      if (!this.camera) {
        console.warn('Camera not found within the rig. Make sure there is an a-camera entity as a child of this entity.');
        return;
      }
      this.cameraObj = this.camera.object3D;
      this.updateCameraPosition();
      
      // Initialize last valid position
      this.lastValidCameraPosition.copy(this.rigObj.position);
    },
  
    updateCameraPosition: function() {
      if (this.cameraObj) {
        // Ensure the camera's position is always valid
        const newY = this.currentHeight - this.rigObj.position.y;
        if (!isNaN(newY)) {
          this.cameraObj.position.y = newY;
        }
      }
    },
 
    isMobileDevice() {
      // Check for user agent patterns common to mobile devices
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      
      // Regular expression for mobile device detection
      // This pattern checks for common mobile platforms and browsers
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      
      // Check if the device has touch points (most mobile devices do)
      const hasTouchScreen = (
        'maxTouchPoints' in navigator && navigator.maxTouchPoints > 0
      ) || (
        'msMaxTouchPoints' in navigator && navigator.msMaxTouchPoints > 0
      );
      
      // Check screen width (most mobile devices are under 768px)
      const hasSmallScreen = window.innerWidth < 768;
      
      // Return true if any mobile indicators are found
      return mobileRegex.test(userAgent) || hasTouchScreen || hasSmallScreen;
    },

    // [Joystick methods remain unchanged]
    createVirtualJoystick: function () {
      if(!this.isMobileDevice()) return;
      this.joystickContainer = document.createElement('div');
      this.joystickContainer.style.position = 'fixed';
      this.joystickContainer.style.width = this.data.joystickSize + 'px';
      this.joystickContainer.style.height = this.data.joystickSize + 'px';
      this.joystickContainer.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
      this.joystickContainer.style.borderRadius = '50%';
      this.joystickContainer.style.zIndex = '9999';
      this.joystickContainer.style.touchAction = 'none';
  
      this.joystickKnob = document.createElement('div');
      this.joystickKnob.style.position = 'absolute';
      this.joystickKnob.style.width = '40%';
      this.joystickKnob.style.height = '40%';
      this.joystickKnob.style.backgroundColor = 'rgba(255, 255, 255, 0.5)';
      this.joystickKnob.style.borderRadius = '50%';
      this.joystickKnob.style.left = '30%';
      this.joystickKnob.style.top = '30%';
  
      this.joystickContainer.appendChild(this.joystickKnob);
      document.body.appendChild(this.joystickContainer);
  
      this.joystickContainer.addEventListener('touchstart', this.onJoystickTouchStart.bind(this), { passive: false });
      this.joystickContainer.addEventListener('touchmove', this.onJoystickTouchMove.bind(this), { passive: false });
      this.joystickContainer.addEventListener('touchend', this.onJoystickTouchEnd.bind(this), { passive: false });
    },
  
    updateJoystickPosition: function () {
      const margin = 20;
      const isLandscape = window.innerWidth > window.innerHeight;
      
      if (isLandscape) {
        this.joystickContainer.style.left = margin + 'px';
        this.joystickContainer.style.bottom = margin + 'px';
      } else {
        this.joystickContainer.style.left = margin + 'px';
        this.joystickContainer.style.bottom = margin + 'px';
      }
      
      // Ensure the joystick is visible
      this.joystickContainer.style.display = 'block';
    },
  
    // [VR and input methods remain unchanged]
    onEnterVR: function () {
      this.isVR = true;
      this.setupVRControllers();
    },
  
    onExitVR: function () {
      this.isVR = false;
    },
  
    setupVRControllers: function () {
      const leftHand = document.querySelector('#leftHand');
      const rightHand = document.querySelector('#rightHand');
      if (leftHand) {
        this.vrHandedness.left = leftHand;
        leftHand.addEventListener('thumbstickmoved', this.onThumbstickMoved.bind(this, 'left'));
      }
      if (rightHand) {
        this.vrHandedness.right = rightHand;
        rightHand.addEventListener('thumbstickmoved', this.onThumbstickMoved.bind(this, 'right'));
      }
    },
  
    onThumbstickMoved: function (hand, event) {
      this.thumbstickAxes[hand].set(event.detail.x, event.detail.y);
    },
  
    onOrientationChange: function () {
      this.isLandscape = window.innerWidth > window.innerHeight;
      this.updateJoystickPosition();
    },
  
    onMouseDown: function (event) {
      this.isMouseDown = true;
    },
  
    onMouseMove: function (event) {
      if (!this.isMouseDown || this.isVR) return;
      this.yaw -= event.movementX * this.data.touchLookSpeed * 0.01;
      this.pitch -= event.movementY * this.data.touchLookSpeed * 0.01;
      this.pitch = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.pitch));
      this.updateCameraRotation();
    },
  
    onMouseUp: function (event) {
      this.isMouseDown = false;
    },
  
    onJoystickTouchStart: function (event) {
      event.preventDefault();
      const touch = event.touches[0];
      this.joystickTouch = touch.identifier;
      this.updateJoystickKnobPosition(touch.clientX, touch.clientY);
      this.isJoystickActive = true;
    },
  
    onJoystickTouchMove: function (event) {
      event.preventDefault();
      if (this.joystickTouch !== null) {
        const touch = Array.from(event.touches).find(t => t.identifier === this.joystickTouch);
        if (touch) {
          this.updateJoystickKnobPosition(touch.clientX, touch.clientY);
        }
      }
    },
  
    onJoystickTouchEnd: function (event) {
      event.preventDefault();
      this.joystickTouch = null;
      this.joystickMovement.set(0, 0);
      this.updateJoystickKnobPosition(0, 0);
      this.joystickKnob.style.transform = 'translate(0px, 0px)';
      this.isJoystickActive = false;
    },
  
    updateJoystickKnobPosition: function (clientX, clientY) {
      const rect = this.joystickContainer.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const maxDistance = rect.width / 2;
      let deltaX = clientX - centerX;
      let deltaY = clientY - centerY;
      const distance = Math.min(maxDistance, Math.sqrt(deltaX * deltaX + deltaY * deltaY));
      const angle = Math.atan2(deltaY, deltaX);
      const knobX = Math.cos(angle) * distance;
      const knobY = Math.sin(angle) * distance;
      this.joystickKnob.style.transform = `translate(${knobX}px, ${knobY}px)`;
      this.joystickMovement.set(knobX / maxDistance, -knobY / maxDistance);
    },
  
    onTouchStart: function (event) {
      if (event.touches.length === 1) {
        const touch = event.touches[0];
        this.isTouching = true;
        this.touchStartX = touch.pageX;
        this.touchStartY = touch.pageY;
      }
    },
  
    onTouchMove: function (event) {
      event.preventDefault();
      if (this.isTouching && event.touches.length === 1) {
        const touch = event.touches[0];
        const deltaX = touch.pageX - this.touchStartX;
        const deltaY = touch.pageY - this.touchStartY;
        if (Math.abs(deltaX) > this.data.touchMoveThreshold || Math.abs(deltaY) > this.data.touchMoveThreshold) {
          this.yaw -= deltaX * this.data.touchLookSpeed * 0.01;
          this.pitch -= deltaY * this.data.touchLookSpeed * 0.01;
          this.pitch = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.pitch));
          this.updateCameraRotation();
          this.touchStartX = touch.pageX;
          this.touchStartY = touch.pageY;
        }
      }
    },
  
    onTouchEnd: function (event) {
      this.isTouching = false;
    },
  
    onKeyDown: function (event) {
      if (event.code in this.movementKeys) {
        this.movementKeys[event.code] = true;
      }
      switch (event.code) {
        case "Space":
          if (this.isGrounded && this.jumpCooldown <= 0) {
            this.velocity.y = this.data.jumpForce;
            this.isJumping = true;
            this.isGrounded = false;
            this.jumpCooldown = 0.3; // Add a small cooldown to prevent rapid jumping
          }
          break;
        case "KeyC":
          this.isCrouching = true;
          break;
      }
    },
  
    onKeyUp: function (event) {
      if (event.code in this.movementKeys) {
        this.movementKeys[event.code] = false;
      }
      if (event.code === "KeyC") {
        this.isCrouching = false;
      }
    },
  
    // Main tick method - this is the key change
    tick: function (time, delta) {
      if (!this.cameraObj) {
        this.onLoaded();
        if (!this.cameraObj) return;
      }
  
      const deltaSeconds = delta / 1000;
      
      // Store the current position before any updates
      this.currentPosition.copy(this.rigObj.position);
      
      // Update horizontal movement first (this sets targetPosition)
      if (this.isVR) {
        this.updateVRMovement(deltaSeconds);
      } else {
        this.updateNonVRMovement(deltaSeconds);
      }
      
      // Apply horizontal movement with smoothing
      // Important: We only apply smoothing to X and Z, not Y
      this.horizontalMovement.set(
        this.targetPosition.x, 
        this.currentPosition.y, // Keep current Y value
        this.targetPosition.z
      );
      
      // Apply smooth horizontal movement
      this.rigObj.position.x = THREE.MathUtils.lerp(this.rigObj.position.x, this.horizontalMovement.x, this.data.horizontalSmoothFactor);
      this.rigObj.position.z = THREE.MathUtils.lerp(this.rigObj.position.z, this.horizontalMovement.z, this.data.horizontalSmoothFactor);
      
      // Then update physics (this directly changes rigObj.position.y)
      this.updatePhysics(deltaSeconds);
      
      // Other updates
      this.updateBobbing(time);
      this.updateCrouching(deltaSeconds);
      
      // Validate position - ensure no NaN values
      if (isNaN(this.rigObj.position.x) || isNaN(this.rigObj.position.y) || isNaN(this.rigObj.position.z)) {
        this.rigObj.position.copy(this.lastValidCameraPosition);
      } else {
        // Store this position as the last valid one
        this.lastValidCameraPosition.copy(this.rigObj.position);
      }
      
      // Ensure camera is updated
      this.updateCameraPosition();
      
      // Ensure joystick is always visible on mobile
      if (this.el.sceneEl.isMobile && this.joystickContainer) {
        this.joystickContainer.style.display = 'block';
      }
    },
  
    updateVRMovement: function (deltaSeconds) {
      const moveSpeed = this.data.speed * deltaSeconds;
      const combinedAxes = new THREE.Vector2(
        this.thumbstickAxes.left.x + this.thumbstickAxes.right.x,
        this.thumbstickAxes.left.y + this.thumbstickAxes.right.y
      ).clampLength(0, 1);
      const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.cameraRig.quaternion);
      const right = new THREE.Vector3(1, 0, 0).applyQuaternion(this.cameraRig.quaternion);
      this.vrMovement.set(0, 0, 0)
        .addScaledVector(forward, -combinedAxes.y * moveSpeed)
        .addScaledVector(right, combinedAxes.x * moveSpeed);
      
      // Only update X and Z of target position
      this.targetPosition.set(
        this.rigObj.position.x + this.vrMovement.x,
        this.rigObj.position.y,
        this.rigObj.position.z + this.vrMovement.z
      );
    },
  
    updateNonVRMovement: function (deltaSeconds) {
      const moveSpeed = this.data.speed * deltaSeconds;
      this.moveVector.set(0, 0, 0);
  
      if (this.el.sceneEl.is('vr-mode')) {
        // VR movement logic (unchanged)
        if (this.movementKeys.KeyW) this.moveVector.z += this.data.movementSpeed;
        if (this.movementKeys.KeyS) this.moveVector.z -= this.data.movementSpeed;
        if (this.movementKeys.KeyA) this.moveVector.x -= this.data.movementSpeed;
        if (this.movementKeys.KeyD) this.moveVector.x += this.data.movementSpeed;
      } else if (this.el.sceneEl.isMobile) {
        // Mobile movement logic (using joystick)
        if (this.isJoystickActive) {
          this.moveVector.x = this.joystickMovement.x * this.data.movementSpeed;
          this.moveVector.z = this.joystickMovement.y * this.data.movementSpeed;
        }
      } else {
        // Desktop movement logic (unchanged)
        if (this.movementKeys.KeyW) this.moveVector.z += this.data.movementSpeed;
        if (this.movementKeys.KeyS) this.moveVector.z -= this.data.movementSpeed;
        if (this.movementKeys.KeyA) this.moveVector.x -= this.data.movementSpeed;
        if (this.movementKeys.KeyD) this.moveVector.x += this.data.movementSpeed;
      }
  
      if (this.moveVector.length() > 0) {
        this.moveVector.normalize().multiplyScalar(moveSpeed);
      }
  
      // Get forward and right vectors from the camera's rotation
      this.cameraObj.getWorldDirection(this.forward);
      this.right.crossVectors(this.forward, this.cameraObj.up);
  
      // Project movement onto the horizontal plane
      this.forward.y = 0;
      this.right.y = 0;
      this.forward.normalize();
      this.right.normalize();
  
      // Calculate movement
      const movement = new THREE.Vector3();
      movement.addScaledVector(this.forward, -this.moveVector.z);
      movement.addScaledVector(this.right, -this.moveVector.x);
  
      // Update target position (only X and Z components)
      if (movement.length() > 0) {
        this.targetPosition.set(
          this.rigObj.position.x + movement.x,
          this.rigObj.position.y,
          this.rigObj.position.z + movement.z
        );
      } else {
        this.targetPosition.copy(this.rigObj.position);
      }
    },
  
    // This is a key fix - now physics operates independently of the lerping
    updatePhysics: function (deltaSeconds) {
      if (!this.cameraObj) return;
  
      // Apply gravity (negative value to pull downward)
      this.velocity.y -= this.data.gravity * deltaSeconds;
      
      // Update Y position based on velocity directly (no lerping)
      this.rigObj.position.y += this.velocity.y * deltaSeconds;
      
      // Ground check
      if (this.rigObj.position.y <= this.groundLevel) {
        this.rigObj.position.y = this.groundLevel;
        if (this.velocity.y < 0) {
          this.velocity.y = 0;
          this.isGrounded = true;
          this.isJumping = false;
        }
      } else {
        this.isGrounded = false;
      }
  
      // Update jump cooldown
      if (this.jumpCooldown > 0) {
        this.jumpCooldown -= deltaSeconds;
      }
    },
  
    updateBobbing: function (time) {
      if (!this.cameraObj) return;
  
      if (this.isGrounded && this.moveVector.length() > 0) {
        this.bobPhase += this.data.bobFrequency * this.moveVector.length() * 0.01;
        const bobOffset = Math.sin(this.bobPhase) * this.data.bobAmount;
        this.cameraObj.position.y += bobOffset;
      }
    },
  
    updateCrouching: function (deltaSeconds) {
      if (!this.cameraObj) return;
  
      const targetHeight = this.isCrouching ? this.data.crouchHeight : this.data.height;
      const heightDiff = targetHeight - this.currentHeight;
      const crouchSpeed = 2;
      if (Math.abs(heightDiff) > 0.01) {
        this.currentHeight += Math.sign(heightDiff) * crouchSpeed * deltaSeconds;
        this.currentHeight = THREE.MathUtils.clamp(
          this.currentHeight,
          this.data.crouchHeight,
          this.data.height
        );
        this.updateCameraPosition();
      }
    },
  
    updateCameraRotation: function () {
      if (!this.isVR && this.cameraObj) {
        this.cameraObj.rotation.set(this.pitch, this.yaw, 0, 'YXZ');
      }
    },
  
    remove: function () {
      window.removeEventListener("keydown", this.onKeyDown);
      window.removeEventListener("keyup", this.onKeyUp);
      document.removeEventListener('mousedown', this.onMouseDown);
      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('mouseup', this.onMouseUp);
      document.removeEventListener('touchstart', this.onTouchStart);
      document.removeEventListener('touchmove', this.onTouchMove);
      document.removeEventListener('touchend', this.onTouchEnd);
      window.removeEventListener('orientationchange', this.onOrientationChange);
      window.removeEventListener('resize', this.onOrientationChange);
      this.el.sceneEl.removeEventListener('enter-vr', this.onEnterVR);
      this.el.sceneEl.removeEventListener('exit-vr', this.onExitVR);
      if (this.vrHandedness.left) {
        this.vrHandedness.left.removeEventListener('thumbstickmoved', this.onThumbstickMoved);
      }
      if (this.vrHandedness.right) {
        this.vrHandedness.right.removeEventListener('thumbstickmoved', this.onThumbstickMoved);
      }
      if (this.joystickContainer) {
        this.joystickContainer.removeEventListener('touchstart', this.onJoystickTouchStart);
        this.joystickContainer.removeEventListener('touchmove', this.onJoystickTouchMove);
        this.joystickContainer.removeEventListener('touchend', this.onJoystickTouchEnd);
        document.body.removeChild(this.joystickContainer);
      }
    }
  });

  export const UserInput = {
    simpleZoom: AFRAME.components['simple-zoom'],
    advancedCameraControls: AFRAME.components['advanced-camera-controls'],
    boundaryConstraint: AFRAME.components['boundary-constraint'],
    unifiedcameracontrols: AFRAME.components['unified-camera-controls'],
    unifiedcameracontrols2: AFRAME.components['unified-camera-controls2'],
  };
